/**
 * Normalizes a PO contact value into recipient data.
 *
 * Upstream contact fields are free text: some hold a real address, others hold
 * whatever was typed into GP — "Jane Doe", "Jane Doe 555-1234", a shop name. Only
 * an `@macmiller.com` address can actually be emailed, so that is the only case
 * this reports as usable.
 *
 * It deliberately does NOT construct an address from a name. The old behavior
 * guessed `First.Last@macmiller.com` and reported it as valid, which meant a
 * plausible-but-wrong address was indistinguishable from a real one — it could be
 * emailed, and printed on a label, with nothing signalling the guess. A contact
 * with no address is now reported as unusable so the UI can ask the user to pick
 * the real person out of the employee directory instead.
 *
 * @param {string} input - The raw contact value from the PO.
 * @returns {object} An object containing:
 *   - `contact`: The address when there is one, else the value tidied for
 *     display (title-cased, so "jane doe" reads as "Jane Doe").
 *   - `isValidEmail`: True ONLY for an `@macmiller.com` address. False means
 *     "not something we can email" — a name, a name with a phone number, or an
 *     off-domain address — and the user has to supply a real one.
 */
/**
 * Whether a contact value is an email address at all, on any domain.
 *
 * Distinct from `isValidEmail`, which is true only for an internal
 * `@macmiller.com` address: an off-domain vendor address is a real, emailable
 * contact even though it isn't an internal one. This is the test for "can we send
 * to this at all", so a name-valued contact is the only thing the UI has to block.
 *
 * @param value - The contact value to test.
 */
export function looksLikeEmailAddress(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());
}

export function generateEmail(input: string): { contact: string, isValidEmail: boolean } {
    const trimmed = (input || '').trim();

    // Already an internal address: the only usable case, passed through untouched.
    const emailRegex = /^[a-zA-Z0-9._%+-]+@macmiller\.com$/;
    if (emailRegex.test(trimmed)) {
        return { contact: trimmed, isValidEmail: true };
    }

    // Looks like an address but is off-domain — kept verbatim (title-casing an
    // address would corrupt it) and still not usable as an internal recipient.
    if (trimmed.includes('@')) {
        return { contact: trimmed, isValidEmail: false };
    }

    // A name or other free text. Tidy it for display; there is no address here.
    const formattedContact = trimmed
        .split(/\s+/) // Split by any whitespace
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize first letter of each word
        .join(' '); // Join words with a space

    return { contact: formattedContact, isValidEmail: false };
}
